import React, { useState, useEffect } from 'react';
import { friendshipService, Friend } from '../services/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { Modal, Button } from 'react-bootstrap';
import './FriendsList.css';

interface FriendsListProps {
  onClose: () => void;
}

const FriendsList: React.FC<FriendsListProps> = ({ onClose }) => {
  const { t } = useSafeTranslation();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [removingFriend, setRemovingFriend] = useState<string | null>(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [friendToRemove, setFriendToRemove] = useState<{ id: string; label: string } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthentication();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadFriends();
    }
  }, [isAuthenticated]);

  const checkAuthentication = () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setIsAuthenticated(false);
      setLoading(false);
      setError(t('friends.loginRequiredMessage', 'You need to be logged in to see your friends list.'));
      return;
    }
    setIsAuthenticated(true);
  };

  const loadFriends = async () => {
    if (!isAuthenticated) {
      return;
    }

    try {
      setLoading(true);
      const friendsList = await friendshipService.getFriendsList();
      setFriends(friendsList);
      setError(null);
    } catch {
      setError(t('friends.errorLoadingFriends', 'Error loading friends list'));
    } finally {
      setLoading(false);
    }
  };

  const handleFriendClick = (friendId: string) => {
    navigate(`/profile/${friendId}`);
    onClose();
  };

  const handleRemoveFriendClick = (friendId: string, nicknameLabel: string) => {
    setFriendToRemove({ id: friendId, label: nicknameLabel });
    setShowRemoveModal(true);
  };

  const handleCancelRemove = () => {
    setShowRemoveModal(false);
    setFriendToRemove(null);
  };

  const handleConfirmRemove = async () => {
    if (!friendToRemove) return;

    try {
      setRemovingFriend(friendToRemove.id);
      await friendshipService.removeFriend(friendToRemove.id);
      toast.success(t('friends.friendRemoved', 'Friend removed successfully!'));
      setFriends(friends.filter(friend => friend.id !== friendToRemove.id));
      setShowRemoveModal(false);
      setFriendToRemove(null);
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('friends.removeFriend', 'Error removing friend'));
    } finally {
      setRemovingFriend(null);
    }
  };

  if (loading) {
    return (
      <div className="friends-list-overlay">
        <div className="friends-list-modal">
          <div className="friends-list-header">
            <h2>{t('friends.myFriends', 'My Friends')}</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="friends-list-content">
            <div className="loading">{t('common.loading', 'Loading...')}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="friends-list-overlay">
        <div className="friends-list-modal">
          <div className="friends-list-header">
            <h2>{t('friends.myFriends', 'My Friends')}</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="friends-list-content">
            <div className="not-authenticated">
              <div className="not-authenticated-icon">🔒</div>
              <h3>{t('friends.loginRequired', 'Login required')}</h3>
              <p>{t('friends.loginRequiredMessage', 'You need to be logged in to see your friends list.')}</p>
              <button onClick={onClose} className="close-modal-button">
                {t('friends.closeModal', 'Close')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="friends-list-overlay">
      <div className="friends-list-modal">
        <div className="friends-list-header">
          <h2>{t('friends.myFriendsCount', 'My Friends ({{count}}/50)').replace('{{count}}', String(friends.length))}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="friends-list-content">
          {error && (
            <div className="error-message">
              {error}
              <button onClick={loadFriends} className="retry-button">
                {t('friends.tryAgain', 'Try Again')}
              </button>
            </div>
          )}
          
          {friends.length === 0 && !error ? (
            <div className="no-friends">
              <div className="no-friends-icon">👥</div>
              <h3>{t('friends.noFriends', 'No friends yet')}</h3>
              <p>{t('friends.noFriendsMessage', 'Send friendship requests to other players to start building your network!')}</p>
            </div>
          ) : (
            <div className="friends-grid">
              {friends.map((friend) => {
                const nick = (friend.nickname || '').trim() || '—';
                return (
                <div key={friend.id} className="friend-card">
                  <div 
                    className="friend-info-clickable"
                    onClick={() => handleFriendClick(friend.id)}
                  >
                    <div className="friend-avatar">
                      {nick.charAt(0).toUpperCase()}
                    </div>
                    <div className="friend-info">
                      <h4 className="friend-name">@{nick}</h4>
                      <div className="friend-level">
                        <span className="level-badge">{t('friends.level', 'Level')} {friend.level}</span>
                        <span className="xp-info">{friend.experiencePoints} XP</span>
                      </div>
                    </div>
                  </div>
                  <button
                    className="remove-friend-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFriendClick(friend.id, nick);
                    }}
                    disabled={removingFriend === friend.id}
                    title={t('friends.removeFriendTitle', 'Remove friend')}
                  >
                    {removingFriend === friend.id ? t('friends.removing', '...') : '×'}
                  </button>
                </div>
              );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Remove Friend Confirmation Modal */}
      <Modal show={showRemoveModal} onHide={handleCancelRemove} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('friends.removeFriendModalTitle', 'Remove Friend')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {friendToRemove && t('friends.removeFriendModalMessage', 'Are you sure you want to remove {{name}} from your friends list? This action cannot be undone.')
              .replace('{{name}}', friendToRemove.label)}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelRemove}>
            {t('friends.removeFriendCancelBtn', 'Cancel')}
          </Button>
          <Button 
            variant="danger" 
            onClick={handleConfirmRemove}
            disabled={removingFriend === friendToRemove?.id}
          >
            {removingFriend === friendToRemove?.id ? t('common.loading', 'Loading...') : t('friends.removeFriendConfirmBtn', 'Remove Friend')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FriendsList; 